<template>
  <div class="texteditor input__container">
    <label v-if="label" class="label input__label" @click="focus">
      {{ label }}
    </label>

    <!-- The text editor field itself -->
    <editor-content
      ref="editorContentElement"
      class="texteditor__container"
      :editor="editor"
    />

    <!-- overlaying stuff, shown when not expanded -->
    <slot>
      <!-- EXPAND - button -->
      <button
        v-if="props.formattable"
        type="button"
        class="texteditor__expandbutton button-icon input__icon"
        @click="expand"
      >
        <lucide-icon name="maximize-2" />
      </button>
    </slot>
  </div>
</template>

<script setup lang="ts">
/**
 * A WYSIWYG text editor.
 */
import {
  ref,
  watch,
  computed,
  onBeforeUnmount,
  onMounted
} from "vue";
import { v4 as uuid } from "uuid";
import { useTexteditor } from "@core/ui/textEditorPopup.store";

// Import the editor
import {
  EditorContent
} from "@tiptap/vue-3";
import Document from "@tiptap/extension-document";
import Text from "@tiptap/extension-text";
import { fullExtensions } from "@core/input/TextEditorExtensions";

import LucideIcon from "@core/ui/LucideIcon.vue";
import type { EditorEvents, EditorOptions } from "@tiptap/core";
import { Editor } from "@tiptap/vue-3";
import type { IconName } from "@core/ui/lucideIcons";

defineOptions({
  name: "InputTexteditor"
});

interface Props {
  id?: string;
  value?: string;
  icons?: IconName[];
  label?: string;
  formattable?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  id: () => `input-${uuid()}`,
  value: "",
  icons: () => [],
  label: "",
  formattable: true
});

const emit = defineEmits<{
  (event: "blur"): void;
  (event: "focus"): void;
  (event: "update:value", value: string): void;
}>();

const editorContentElement = ref<InstanceType<typeof EditorContent>>();

const editorClassName = computed(() => {
  return "input input-text input__element formatted-text formatted-text--compact";
});

const editorStyle = computed(() => {
  return (
    "padding-right:" +
    (props.icons.length * 2 + 1) +
    "rem; padding-bottom: 2rem;"
  );
});

const extensions = computed(() => {
  if (props.formattable) {
    return fullExtensions;
  } else {
    return [
      Document.extend({
        content: "inline*"
      }),
      Text
    ];
  }
});

const editorOptions = computed((): Partial<EditorOptions> => ({
  autofocus: false,
  editorProps: {
    attributes: {
      id: props.id,
      class: editorClassName.value,
      style: editorStyle.value
    }
  },
  parseOptions: {
    preserveWhitespace: true
  },
  content: props.value,
  extensions: extensions.value,
  onFocus: () => emit("focus"),
  onBlur: () => emit("blur"),
  onUpdate: onUpdate
}));

const editor = ref<Editor | undefined>();
onMounted(() => {
  editor.value = new Editor(editorOptions.value);
});
function reloadEditor() {
  if (editor.value != null) {
    editor.value.destroy();
  }
  editor.value = new Editor(editorOptions.value);
}

function getText() {
  if (isEditorEmpty()) return "";
  if (editor.value == null) return "";
  if (props.formattable) return editor.value.getHTML();
  else return editor.value.getText();
}

watch(
  () => props.value,
  (val: string) => {
    if (editor.value != null) {
      if (getText() !== val) {
        editor.value.chain().setContent(val, { parseOptions: { preserveWhitespace: true }}).run();
      }
    }
  },
  { immediate: true }
);

watch(
  () => props.icons,
  () => {
    if (editor.value == null) return;
    editor.value.setOptions(editorOptions.value);
  }
);

watch(extensions, () => {
  reloadEditor();
})

function focus() {
  if (editor.value == null) return;
  try {
    editor.value.chain().focus().run();
  } catch (error) {
    console.error("Error focusing editor:", error);
  }
}
function blur() {
  if (editor.value == null) return;
  try {
    editor.value.chain().blur().run();
  } catch (error) {
    console.error("Error blurring editor:", error);
  }
}

function onUpdate(editorProps: EditorEvents["update"]) {
  let newText = getText();
  if (newText === props.value) return;
  emit("update:value", newText);
}

function isEditorEmpty() {
  if (editor?.value == null) return true;
  const doc = editor.value?.getJSON();
  if (doc.type !== "doc") return true;
  if (doc.content == null || doc.content.length === 0) return true;
  if (doc.content.length > 1) return false;
  if (doc.content[0].type !== "paragraph") return false;
  const paragraph = doc.content[0];
  if (paragraph.content == null || paragraph.content.length === 0) return true;
  return false;
}

// Expand

const expandedEditorPopup = useTexteditor();

function expand() {
  expandedEditorPopup.open({
    handleId: props.id,
    value: props.value,
    onUpdate: (newText) => {
      if (newText === props.value) return;
      emit("update:value", newText);
    },
    onClose: () => {
      focus();
    }
  });
}

onBeforeUnmount(() => {
  if (expandedEditorPopup.handleId == props.id) {
    expandedEditorPopup.close();
  }
});

// =======================
// EXPOSE

defineExpose({
  focus,
  blur,
  expand
});
</script>

<style lang="scss">
.texteditor {
  &__expandbutton {
    position: absolute;
    right: 1rem;
    bottom: 0;
    z-index: 1;
    padding-bottom: 5px;
  }

  // Move the pencil icon out of the way
  // if the user has the browser extension
  // "LanguageTool" installed:
  html[data-lt-installed="true"] &__expandbutton {
    right: 1.75rem;
    bottom: 2px;
  }

  &__container {
    color: var(--color-text);
    .ProseMirror {
      // IMPORTANT! TEXT EDITOR DEPENDS ON PRE-WRAP.
      white-space: pre-wrap;
      min-height: 7em;

      @supports (-moz-appearance: none) {
        // only firefox supports this fully
        // and it interferes with colors in chrome
        scrollbar-width: thin;
      }
    }
  }
}
</style>
